export type ImageDimensions = {
  width: number;
  height: number;
};

export function responsiveImageWidths(
  source: unknown,
  maxWidth: number,
  candidates: readonly number[] = [320, 480, 640, 768, 1024, 1280],
): number[] {
  const requestedMax = Math.max(1, Math.round(maxWidth));
  const intrinsicWidth = getImageDimensions(source)?.width;
  const limit = Math.max(1, Math.min(requestedMax, intrinsicWidth ?? requestedMax));
  const widths = candidates
    .map((width) => Math.round(width))
    .filter((width) => width > 0 && width <= limit);
  widths.push(limit);
  return [...new Set(widths)].sort((a, b) => a - b);
}

export function buildImageSrcSet(
  widths: readonly number[],
  makeUrl: (width: number) => string,
): string {
  return widths.map((width) => `${makeUrl(width)} ${width}w`).join(', ');
}

function positiveNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function fromRecord(value: unknown): ImageDimensions | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const width = positiveNumber(record.width);
  const height = positiveNumber(record.height);
  if (width && height) return { width, height };
  return fromRecord(record.dimensions);
}

/**
 * Reads dimensions returned by Sanity, with the encoded asset-ref dimensions
 * as a fallback. Sanity image refs include the original pixel dimensions (for
 * example, image-abc-1920x1080-jpg), so this does not invent a layout ratio.
 */
export function getImageDimensions(source: unknown): ImageDimensions | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const record = source as Record<string, unknown>;

  const direct = fromRecord(record);
  if (direct) return direct;

  const metadata = fromRecord(record.metadata);
  if (metadata) return metadata;

  const asset = record.asset;
  if (asset && typeof asset === 'object') {
    const assetRecord = asset as Record<string, unknown>;
    const assetMetadata = fromRecord(assetRecord.metadata);
    if (assetMetadata) return assetMetadata;

    const ref = typeof assetRecord._ref === 'string' ? assetRecord._ref : '';
    const match = ref.match(/-(\d+)x(\d+)-/);
    if (match) return { width: Number(match[1]), height: Number(match[2]) };
  }

  return undefined;
}

export function renderedImageDimensions(
  source: unknown,
  maxWidth?: number,
): ImageDimensions | undefined {
  const dimensions = getImageDimensions(source);
  if (!dimensions) return undefined;
  const width = maxWidth && maxWidth > 0 ? Math.min(dimensions.width, maxWidth) : dimensions.width;
  return {
    width,
    height: Math.max(1, Math.round((width / dimensions.width) * dimensions.height)),
  };
}

export function imageAspectRatio(source: unknown): string | undefined {
  const dimensions = getImageDimensions(source);
  return dimensions ? `${dimensions.width} / ${dimensions.height}` : undefined;
}
